import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import _isEmpty from 'lodash/isEmpty';
import _reduce from 'lodash/reduce';
import _flatMap from 'lodash/flatMap';

// Components
import EntityHeader from '../EntityHeaderV2';
import RsvpsList from './RsvpsList';
import GotItTooltip from './ToggleTooltip';
import EditPageHeader from '../EditPageCommon/EditPageHeader';
import EditPageAddEntity from '../EditPageCommon/EditPageAddEntity';
import EditPageForm from '../EditPageCommon/EditPageForm';

// Actions
import { showModal } from '../../../../../actions/ModalActions';
import { togglePage } from '../../../../../actions/website/PageActions';
import { moveEntity } from '../../../../../actions/website/EntityActions';
import { editEvent } from '../../../../../actions/website/entities/RsvpActions';
import { updateEventV2 } from '../../../../../actions/website/entities/EventActions';

// Utils
import FeatureFlags from '../../../../../util/featureFlags';

// Flags
const blockRSVPPublish = FeatureFlags.get('blockRSVPPublish');
const isCmsPageFormV2 = FeatureFlags.get('isCmsPageFormV2');

const PageHeader = isCmsPageFormV2 ? EditPageHeader : EntityHeader;

const RsvpEdit = ({
  guests,
  page,
  isPageHidden,
  themeKey,
  orderedIds,
  rsvpEntities,
  openEventFormModal,
  showErrorModal,
  showSuccessModal,
  onPageToggle,
  isGotItTooltip,
  closeGotItTooltip,
  onSortEnd,
  onEdit,
  updateEvent,
}) => {

  const checkToggleAllowed = (pageId) => {
    const noEvents = orderedIds.length === 0;
    const noGuests = _isEmpty(guests.byId);
    const numGuestsInvited = _reduce(guests.orderedIds, (sum, guestGroupId) => {
      const guestGroup = guests.byId[guestGroupId];
      const eventInvitations = _flatMap(guestGroup.guests, guest => guest.event_invitations);
      return sum + eventInvitations.length;
    }, 0);
    const noGuestsInvited = numGuestsInvited === 0;
    const rsvpDisabled = orderedIds.filter(id => rsvpEntities[id].collect_rsvps === true).length === 0;


    if (noEvents) {
      const errorModalProps = {
        heading: 'Want guests to RSVP on your wedding website?',
        body: 'First, create an event and build a guest list for it. Then you\'ll be able to unhide this page and let guests RSVP on this page',
        button: {
          text: 'Create An Event',
          action: openEventFormModal
        }
      };
      showErrorModal(errorModalProps);
    } else if (noGuests) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'First, build your Guest Directory.',
        button: {
          text: 'Go To Guest Directory',
          action: () => browserHistory.push('/wedding/manage/guests/all')
        }
      };
      showErrorModal(errorModalProps);
    } else if (noGuestsInvited) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'First, choose some to invite.',
        button: {
          text: 'Invite Guests',
          action: () => browserHistory.push('/wedding/manage/guests/build')
        }
      };
      showErrorModal(errorModalProps);
    } else if (rsvpDisabled) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'Turn on online RSVPs for at least one of your events (and make sure guests have already been invited).',
      };
      showErrorModal(errorModalProps);
    } else {
      onPageToggle(pageId);
      showSuccessModal();
    }
  };

  const handlePageToggle = (pageId) => {
    if (!isPageHidden) {
      onPageToggle(pageId);
    } else if (blockRSVPPublish) {
      checkToggleAllowed(pageId);
    } else {
      onPageToggle(pageId);
      showSuccessModal();
    }
  };

  return (
    <div>
      <PageHeader
        page={page}
        themeKey={themeKey}
        onAdd={openEventFormModal}
        onPageToggle={handlePageToggle}
        entityName="Event"
        isGotItTooltip={isGotItTooltip}
        closeGotItTooltip={closeGotItTooltip}
        gotItTooltip={GotItTooltip({ closeTooltip: closeGotItTooltip })}
      />
      {!isCmsPageFormV2 && rsvpEntities && (
        <RsvpsList
          rsvpEntities={rsvpEntities}
          orderedIds={orderedIds}
          onSortEnd={onSortEnd}
          onEdit={onEdit}
          updateEvent={updateEvent}
          useDragHandle
          lockAxis="y"
        />
      )}
      {isCmsPageFormV2 && (
        <>
          <EditPageForm page={page} />
          <EditPageAddEntity
            entityName="Event"
            onAdd={openEventFormModal}
            onAddText="Add An Event"
          >
            {rsvpEntities && (
              <RsvpsList
                rsvpEntities={rsvpEntities}
                orderedIds={orderedIds}
                onSortEnd={onSortEnd}
                onEdit={onEdit}
                updateEvent={updateEvent}
                useDragHandle
                lockAxis="y"
              />
            )}
          </EditPageAddEntity>
        </>
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  guests: state.guestList.guests,
});

const mapDispatchToProps = dispatch => ({
  onPageToggle: pageId => dispatch(togglePage(pageId)),
  showSuccessModal: () => dispatch(showModal('PUBLISH_RSVP', {}, { size: 'md' })),
  showErrorModal: errorProps => dispatch(showModal('PUBLISH_PAGE_ERROR', { ...errorProps }, { size: 'md' })),
  onSortEnd: ({ oldIndex, newIndex }) => dispatch(moveEntity(oldIndex, newIndex)),
  onEdit: (rsvp) => {
    dispatch(editEvent(rsvp));
    dispatch(showModal('EVENT_FORM', { websiteEvent: true, formIdentifier: 'eventForm' }, { size: 'md' }));
  },
  updateEvent: (event) => dispatch(updateEventV2(event)),
});

RsvpEdit.propTypes = {
  page: PropTypes.shape({}),
  isPageHidden: PropTypes.bool,
  theme: PropTypes.shape({ key: PropTypes.string, font_name: PropTypes.string }),
  orderedIds: PropTypes.arrayOf(PropTypes.number),
  rsvpEntities: PropTypes.shape({}),
  openEventFormModal: PropTypes.func,
  showSuccessModal: PropTypes.func,
  onPageToggle: PropTypes.func,
  isGotItTooltip: PropTypes.bool,
  closeGotItTooltip: PropTypes.func,
  onSortEnd: PropTypes.func,
  onEdit: PropTypes.func,
  updateEvent: PropTypes.func,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RsvpEdit);



// WEBPACK FOOTER //
// ./src/components/manage/website/entities/Rsvp/RsvpEdit.jsx